import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

type Props = {
  open: boolean;
  memberName: string;
  role: string;
  pending: boolean;
  error: string;
  onRoleChange: (role: string) => void;
  onCancel: () => void;
  onSave: () => void;
};

export default function ProjectChangeAccessDialog({ open, memberName, role, pending, error, onRoleChange, onCancel, onSave }: Props) {
  const { t, i18n } = useTranslation();
  const roleFieldRef = useRef<HTMLDivElement>(null);
  const uiRole = role === "modifier" ? "editor" : role;
  const preventPendingDismissal = (event: Event) => {
    if (pending) event.preventDefault();
  };
  const descriptionKey = {
    admin: "projects.adminAccessDescription",
    editor: "projects.editAccessDescription",
    viewer: "projects.viewerAccessDescription",
  }[uiRole];
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !pending) onCancel(); }}>
      <DialogContent dir={i18n.dir()} showCloseButton={false}
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[400px]"
        onOpenAutoFocus={(event) => { event.preventDefault(); roleFieldRef.current?.querySelector<HTMLButtonElement>('[role="combobox"]')?.focus(); }}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={preventPendingDismissal} onInteractOutside={preventPendingDismissal}>
        <header className="shrink-0 border-b p-4">
          <DialogTitle>{t("projects.changeAccessLevel")}</DialogTitle>
        </header>
        <form className="flex min-h-0 flex-col" onSubmit={(event) => { event.preventDefault(); if (!pending) onSave(); }}>
          <div className="min-h-0 space-y-4 overflow-y-auto p-4">
            <DialogDescription className="break-words">{t("projects.selectAccessLevelForUser", { name: memberName })}</DialogDescription>
            {error ? <p id="change-access-error" role="alert" className="text-sm text-destructive dark:text-destructive-foreground">{error}</p> : null}
            <div ref={roleFieldRef} className="space-y-2">
              <label htmlFor="change-access-role" className="text-sm font-medium">{t("projects.accessLevel")}</label>
              <Select dir={i18n.dir()} value={uiRole} disabled={pending} onValueChange={onRoleChange}>
                <SelectTrigger id="change-access-role" className="w-full" aria-invalid={Boolean(error)} aria-describedby={error ? "change-access-error" : undefined}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("aiCatalog.admin")}</SelectItem>
                  <SelectItem value="editor">{t("aiCatalog.editor")}</SelectItem>
                  <SelectItem value="viewer">{t("aiCatalog.viewer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {descriptionKey ? <p className="text-sm text-muted-foreground">{t(descriptionKey)}</p> : null}
          </div>
          <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t p-4">
            <Button type="button" variant="ghost" disabled={pending} onClick={onCancel}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={pending}>{pending ? <Spinner className="size-4 motion-reduce:animate-none" /> : null}{pending ? t("common.saving") : t("common.save")}</Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
